import { useState, useEffect, forwardRef, useCallback } from 'react'
import './Calendar.css';
import { SetAuditTrail, SetAuditTrailBulk } from '../Reports/AuditTrailReportEmployeeSetup';
import 'react-datepicker/dist/react-datepicker.css';
import { registerLocale } from 'react-datepicker';
import en from 'date-fns/locale/en-US';
import { token, domain } from '../Security';
import axios from '../../api/apiClient';

import { CalendarHeader } from "./components/CalendarHeader";
import { CalendarBody } from "./components/CalendarBody";
import { AddGeneralAnnouncementForm } from './forms/AddGeneralAnnouncementForm';
import { AddGeneralMemoForm } from './forms/AddGeneralMemoForm';
import { DeleteAnnouncementForm } from './forms/DeleteAnnouncementForm';
import { DeleteMemoForm } from './forms/DeleteMemoForm';
import { ViewEventsAnnouncementForm } from './forms/ViewEventsAnnouncementForm';
import { ViewEventsMemoForm } from './forms/ViewEventsMemoForm';
import { ViewAllEventsAnnouncementForm } from './forms/ViewAllEventsAnnouncementForm';
import { ViewAllEventsMemoForm } from './forms/ViewAllEventsMemoForm';
import { LoadingSpinner } from '../../common/Loading';
import { GeneralMessage, useGeneralMessage } from '../../common/APICommon';
import { AddEditAnnouncementForm } from './forms/AddEditAnnouncementForm';
import { AddEditMemoForm } from './forms/AddEditMemoForm';
import { AddEditEmployeeMemoForm } from './forms/AddEditEmployeeMemoForm';
import { ViewEventsEmployeeMemoForm } from './forms/ViewEventsEmployeeMemoForm';
import { ViewAllEventsEmployeeMemoForm } from './forms/ViewAllEventsEmployeeMemoForm';
import { DeleteEmployeeMemoForm } from './forms/DeleteEmployeeMemoForm';
import { MonthYearPicker } from './components/MonthYearPicker';

registerLocale('en', en);

function Calendar({ user, userAccess, isHideSideMenu }) {
	const [loadingRequest, setLoadingRequest] = useState(false);
	const [selectedDate, setSelectedDate] = useState(new Date());

	// SHARED STATE VARIABLES
	const [description, setDescription] = useState("");
	const [date, setDate] = useState("");
	const [dateFrom, setDateFrom] = useState("");
	const [dateTo, setDateTo] = useState("");
	const [contentAnnouncement, setContentAnnouncement] = useState("");
	const [contentMemo, setContentMemo] = useState("")
	const [isActive, setIsActive] = useState(true);
	const [filesAnnouncement, setFilesAnnouncement] = useState("");
	const [filesMemo, setFilesMemo] = useState("");

	// DATA CONTAINERS
	const [editDataAnnouncement, setEditDataAnnouncement] = useState([]);
	const [editDataMemo, setEditDataMemo] = useState([]);
	const [editDataEmployeeMemo, setEditDataEmployeeMemo] = useState([]);
	const [generalData, setGeneralData] = useState([]);

	const [eventDate, setEventDate] = useState("");

	// MODAL TOGGLES
	const [showEditAnnouncement, setShowEditAnnouncement] = useState(false);
	const [showEditMemo, setShowEditMemo] = useState(false);
	const [isUpdateAnnouncement, setIsUpdateAnnouncement] = useState(false);
	const [isRefreshEvents, setIsRefreshEvents] = useState(false);

	const [showDeleteAnnouncement, setShowDeleteAnnouncement] = useState(false);
	const [showDeleteMemo, setShowDeleteMemo] = useState(false);
	const [isUpdateMemo, setIsUpdateMemo] = useState(false);

	// GENERAL ANNOUNCEMENT
	const [descriptionGeneralAnnouncement, setDescriptionGeneralAnnouncement] = useState("");
	const [contentGeneralAnnouncement, setContentGeneralAnnouncement] = useState("");
	const [filesGeneralAnnouncement, setFilesGeneralAnnouncement] = useState("");

	// GENERAL MEMO
	const [descriptionGeneralMemo, setDescriptionGeneralMemo] = useState("");
	const [contentGeneralMemo, setContentGeneralMemo] = useState("");
	const [filesGeneralMemo, setFilesGeneralMemo] = useState("");

	const [showAnnouncementForm, setShowAnnouncementForm] = useState(false);
	const [showMemoForm, setShowMemoForm] = useState(false);
	const [showGeneralAnnouncementForm, setShowGeneralAnnouncementForm] = useState(false);
	const [showGeneralMemoForm, setShowGeneralMemoForm] = useState(false);

	const [showEventsAnnouncement, setShowEventsAnnouncement] = useState(false);
	const [showEventsMemo, setShowEventsMemo] = useState(false);
	const [showAllEventsAnnouncement, setShowAllEventsAnnouncement] = useState(false);
	const [showAllEventsMemo, setShowAllEventsMemo] = useState(false);

	// Employee Specific
	const [showEmployeeMemoForm, setShowEmployeeMemoForm] = useState(false);
	const [showEventsEmployeeMemo, setShowEventsEmployeeMemo] = useState(false);
	const [showAllEventsEmployeeMemo, setShowAllEventsEmployeeMemo] = useState(false);
	const [showEditEmployeeMemo, setShowEditEmployeeMemo] = useState(false);
	const [showDeleteEmployeeMemo, setShowDeleteEmployeeMemo] = useState(false);
	const [isUpdateEmployeeMemo, setIsUpdateEmployeeMemo] = useState(false);
	const [selectedEmployeePersonalInformationId, setSelectedEmployeePersonalInformationId] = useState(null);
	const [employees, setEmployees] = useState([]);

	// ─── PATCHED: memoTypeId replaces description for Employee Memo ───────────
	const [selectedMemoTypeId, setSelectedMemoTypeId] = useState(null);
	const [memoTypes, setMemoTypes] = useState([]);

	const fetchMemoTypes = useCallback(async () => {
		try {
			const response = await axios.get(`${domain}/api/MemoType`, {
				headers: { 'Authorization': `Bearer ${token}` }
			});
			setMemoTypes(response.data.filter(mt => mt.isActive === true));
		} catch (error) {
			console.error('Error fetching memo types:', error);
		}
	}, []);

	useEffect(() => {
		fetchMemoTypes();
	}, [fetchMemoTypes]);
	// ──────────────────────────────────────────────────────────────────────────

	// ─── PATCHED: CC state ────────────────────────────────────────────────────
	const [ccList, setCcList] = useState([]);
	// ──────────────────────────────────────────────────────────────────────────

	const { showMessage, message, isSuccess, showGeneralMessage } = useGeneralMessage(2000);

	// --- UNIVERSAL RESET FUNCTION ---
	const resetFormFields = () => {
		setDescription("");
		setDate("");
		setDateFrom("");
		setDateTo("");
		setContentAnnouncement("");
		setContentMemo("");
		setFilesAnnouncement([]);
		setFilesMemo([]);
		setSelectedEmployeePersonalInformationId(null);
		setEditDataAnnouncement([]);
		setEditDataMemo([]);
		setEditDataEmployeeMemo([]);
		// ─── PATCHED: also reset memoTypeId ──────────────────────────────────
		setSelectedMemoTypeId(null);
		// ─────────────────────────────────────────────────────────────────────
		// ─── PATCHED: also reset CC ───────────────────────────────────────────
		setCcList([]);
		// ─────────────────────────────────────────────────────────────────────
	};

	// --- GENERAL HANDLERS ---
	const handleSubmitGeneralAnnouncementCreate = async (event) => {
		event.preventDefault();
		const apiUrl = domain + "/api/General";

		const toBase64 = (file) => new Promise((resolve, reject) => {
			const reader = new FileReader();
			reader.readAsDataURL(file);
			reader.onload = () => resolve(reader.result);
			reader.onerror = error => reject(error);
		});

		let base64String = "";
		let photoToBeSave = "";

		if (filesGeneralAnnouncement.length > 0) {
			base64String = await toBase64(filesGeneralAnnouncement[0]);
			photoToBeSave = base64String.split(',')[1];
		}

		const data = {
			key: "Announcement",
			title: descriptionGeneralAnnouncement,
			description: contentGeneralAnnouncement,
			content: filesGeneralAnnouncement ? photoToBeSave : null,
		};

		try {
			setLoadingRequest(true);
			await axios.post(apiUrl, data, {
				headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }
			});
			setShowGeneralAnnouncementForm(false);
			await SetAuditTrail({ setupArea: "Calendar", actionTaken: "insert", field: "Add", newValue: `${data.title}`, oldValue: "-", dateSaved: new Date(), savedById: user.id });
			showGeneralMessage("Request Success!", true);
			setDescriptionGeneralAnnouncement("");
			setContentGeneralAnnouncement("");
			setFilesGeneralAnnouncement("");
		} catch (error) {
			console.error('Error:', error);
			const errorMessage = JSON.parse(error.response.request.responseText).message;
			showGeneralMessage(errorMessage, false);
		} finally {
			setLoadingRequest(false);
		}
	};

	const handleSubmitGeneralAnnouncementUpdate = async (event) => {
		event.preventDefault();
		const apiUrl = domain + "/api/General";

		const toBase64 = (file) => new Promise((resolve, reject) => {
			const reader = new FileReader();
			reader.readAsDataURL(file);
			reader.onload = () => resolve(reader.result);
			reader.onerror = error => reject(error);
		});

		let base64String = "";
		let photoToBeSave = "";

		if (filesGeneralAnnouncement.length > 0) {
			base64String = await toBase64(filesGeneralAnnouncement[0]);
			photoToBeSave = base64String.split(',')[1];
		}

		const data = {
			key: "Announcement",
			title: descriptionGeneralAnnouncement,
			description: contentGeneralAnnouncement,
			content: filesGeneralAnnouncement ? photoToBeSave : null,
		};

		try {
			setLoadingRequest(true);
			await axios.put(`${apiUrl}/${generalData.id}`, data, {
				headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }
			});
			setShowGeneralAnnouncementForm(false);
			showGeneralMessage("Request Success!", true);
			setDescriptionGeneralAnnouncement("");
			setContentGeneralAnnouncement("");
			setFilesGeneralAnnouncement("");
			setGeneralData([]);
		} catch (error) {
			console.error('Error:', error);
			const errorMessage = JSON.parse(error.response.request.responseText).message;
			showGeneralMessage(errorMessage, false);
		} finally {
			setLoadingRequest(false);
		}
	};

	const handleSubmitGeneralDelete = async (event) => {
		event.preventDefault();
		const apiUrl = domain + "/api/General";
		if (generalData === "" || generalData === null || generalData === undefined || generalData.length <= 0) {
			showGeneralMessage("Nothing to clear!", false);
			return;
		}
		try {
			setLoadingRequest(true);
			await axios.delete(`${apiUrl}/${generalData.id}`, {
				headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }
			});
			await SetAuditTrail({ setupArea: "Calendar", actionTaken: "delete", field: "Clear Announcement/ Memo", newValue: `-`, oldValue: `${generalData.title}`, dateSaved: new Date(), savedById: user.id });
			showGeneralMessage("Request Success!", true);
			setDescriptionGeneralAnnouncement("");
			setContentGeneralAnnouncement("");
			setFilesGeneralAnnouncement("");
			setDescriptionGeneralMemo("");
			setContentGeneralMemo("");
			setFilesGeneralMemo("");
			setGeneralData([]);
		} catch (error) {
			console.error('Error:', error);
			const errorMessage = JSON.parse(error.response.request.responseText).message;
			showGeneralMessage(errorMessage, false);
		} finally {
			setLoadingRequest(false);
		}
	};

	const handleSubmitGeneralMemoCreate = async (event) => {
		event.preventDefault();
		const apiUrl = domain + "/api/General";
		const toBase64 = (file) => new Promise((resolve, reject) => {
			const reader = new FileReader();
			reader.readAsDataURL(file);
			reader.onload = () => resolve(reader.result);
			reader.onerror = error => reject(error);
		});
		let base64String = "";
		let photoToBeSave = "";
		if (filesGeneralMemo.length > 0) {
			base64String = await toBase64(filesGeneralMemo[0]);
			photoToBeSave = base64String.split(',')[1];
		}
		const data = {
			key: "Memo",
			title: descriptionGeneralMemo,
			description: contentGeneralMemo,
			content: filesGeneralMemo ? photoToBeSave : null,
		};
		try {
			setLoadingRequest(true);
			await axios.post(apiUrl, data, {
				headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }
			});
			setShowGeneralMemoForm(false);
			await SetAuditTrail({ setupArea: "Calendar", actionTaken: "insert", field: "Add", newValue: `${data.title}`, oldValue: "-", dateSaved: new Date(), savedById: user.id });
			showGeneralMessage("Request Success!", true);
			setDescriptionGeneralMemo("");
			setContentGeneralMemo("");
			setFilesGeneralMemo("");
		} catch (error) {
			const errorMessage = JSON.parse(error.response.request.responseText).message;
			showGeneralMessage(errorMessage, false);
		} finally {
			setLoadingRequest(false);
		}
	};

	const handleSubmitGeneralMemoUpdate = async (event) => {
		event.preventDefault();
		const apiUrl = domain + "/api/General";
		const toBase64 = (file) => new Promise((resolve, reject) => {
			const reader = new FileReader();
			reader.readAsDataURL(file);
			reader.onload = () => resolve(reader.result);
			reader.onerror = error => reject(error);
		});
		let base64String = "";
		let photoToBeSave = "";
		if (filesGeneralMemo.length > 0) {
			base64String = await toBase64(filesGeneralMemo[0]);
			photoToBeSave = base64String.split(',')[1];
		}
		const data = {
			key: "Memo",
			title: descriptionGeneralMemo,
			description: contentGeneralMemo,
			content: filesGeneralMemo ? photoToBeSave : null,
		};
		try {
			setLoadingRequest(true);
			await axios.put(`${apiUrl}/${generalData.id}`, data, {
				headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }
			});
			setShowGeneralMemoForm(false);
			showGeneralMessage("Request Success!", true);
			setDescriptionGeneralMemo("");
			setContentGeneralMemo("");
			setFilesGeneralMemo("");
			setGeneralData([]);
		} catch (error) {
			const errorMessage = JSON.parse(error.response.request.responseText).message;
			showGeneralMessage(errorMessage, false);
		} finally {
			setLoadingRequest(false);
		}
	};

	// --- ANNOUNCEMENT HANDLERS ---
	const handleSubmitAnnouncement = async (event) => {
		event.preventDefault();
		const apiUrl = domain + "/api/Announcement";
		const toBase64 = (file) => new Promise((resolve, reject) => {
			const reader = new FileReader();
			reader.readAsDataURL(file);
			reader.onload = () => resolve(reader.result);
			reader.onerror = error => reject(error);
		});
		let base64String = "";
		let photoToBeSave = "";
		if (filesAnnouncement.length > 0) {
			base64String = await toBase64(filesAnnouncement[0]);
			photoToBeSave = base64String.split(',')[1];
		}
		const data = {
			title: description,
			dateFrom: dateFrom,
			dateTo: dateTo,
			description: contentAnnouncement,
			content: filesAnnouncement ? photoToBeSave : null,
		};
		try {
			setLoadingRequest(true);
			await axios.post(apiUrl, data, {
				headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }
			});
			setShowAnnouncementForm(false);
			await SetAuditTrail({ setupArea: "Calendar", actionTaken: "insert", field: "Add", newValue: `${data.title}`, oldValue: "-", dateSaved: new Date(), savedById: user.id });
			showGeneralMessage("Request Success!", true);
			resetFormFields();
			setIsRefreshEvents(true);
		} catch (error) {
			console.error('Error:', error);
			const errorMessage = JSON.parse(error.response.request.responseText).message;
			showGeneralMessage(errorMessage, false);
		} finally {
			setLoadingRequest(false);
		}
	};

	const handleUpdateAnnouncement = async (event) => {
		event.preventDefault();
		const apiUrl = domain + "/api/Announcement";
		const toBase64 = (file) => new Promise((resolve, reject) => {
			const reader = new FileReader();
			reader.readAsDataURL(file);
			reader.onload = () => resolve(reader.result);
			reader.onerror = error => reject(error);
		});
		let base64String = "";
		let photoToBeSave = "";
		if (filesAnnouncement.length > 0) {
			base64String = await toBase64(filesAnnouncement[0]);
			photoToBeSave = base64String.split(',')[1];
		}
		const data = {
			title: description,
			dateFrom: dateFrom,
			dateTo: dateTo,
			description: contentAnnouncement,
			content: filesAnnouncement ? photoToBeSave : null,
		};
		try {
			setLoadingRequest(true);
			await axios.put(`${apiUrl}/${editDataAnnouncement.id}`, data, {
				headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }
			});
			setShowEditAnnouncement(false);
			showGeneralMessage("Request Success!", true);
			resetFormFields();
			setIsUpdateAnnouncement(true);
			setIsRefreshEvents(true);
		} catch (error) {
			console.error('Error:', error);
			const errorMessage = JSON.parse(error.response.request.responseText).message;
			showGeneralMessage(errorMessage, true);
		} finally {
			setLoadingRequest(false);
		}
	};

	const handleDeleteAnnouncement = async (event) => {
		event.preventDefault();
		const apiUrl = domain + "/api/Announcement";
		try {
			setLoadingRequest(true);
			await axios.delete(`${apiUrl}/${editDataAnnouncement.id}`, {
				headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }
			});
			setShowDeleteAnnouncement(false);
			await SetAuditTrail({ setupArea: "Calendar", actionTaken: "delete", field: "Delete", newValue: `-`, oldValue: `${editDataAnnouncement.title}`, dateSaved: new Date(), savedById: user.id });
			showGeneralMessage("Request Success!", true);
			resetFormFields();
			setIsUpdateAnnouncement(true);
			setIsRefreshEvents(true);
		} catch (error) {
			console.error('Error:', error);
			const errorMessage = JSON.parse(error.response.request.responseText).message;
			showGeneralMessage(errorMessage, false);
		} finally {
			setLoadingRequest(false);
		}
	};

	// --- MEMO HANDLERS ---
	const handleSubmitMemo = async (event) => {
		event.preventDefault();
		const apiUrl = domain + "/api/Memo";
		const toBase64 = (file) => new Promise((resolve, reject) => {
			const reader = new FileReader();
			reader.readAsDataURL(file);
			reader.onload = () => resolve(reader.result);
			reader.onerror = error => reject(error);
		});
		let base64String = "";
		let photoToBeSave = "";
		if (filesMemo.length > 0) {
			base64String = await toBase64(filesMemo[0]);
			photoToBeSave = base64String.split(',')[1];
		}
		const data = {
			title: description,
			dateFrom: dateFrom,
			dateTo: dateTo,
			description: contentMemo,
			content: filesMemo ? photoToBeSave : null,
		};
		try {
			setLoadingRequest(true);
			await axios.post(apiUrl, data, {
				headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }
			});
			setShowMemoForm(false);
			await SetAuditTrail({ setupArea: "Calendar", actionTaken: "insert", field: "Add", newValue: `${data.title}`, oldValue: "-", dateSaved: new Date(), savedById: user.id });
			showGeneralMessage("Request Success!", true);
			resetFormFields();
			setIsRefreshEvents(true);
		} catch (error) {
			console.error('Error:', error);
			const errorMessage = JSON.parse(error.response.request.responseText).message;
			showGeneralMessage(errorMessage, false);
		} finally {
			setLoadingRequest(false);
		}
	};

	const handleUpdateMemo = async (event) => {
		event.preventDefault();
		const apiUrl = domain + "/api/Memo";
		const toBase64 = (file) => new Promise((resolve, reject) => {
			const reader = new FileReader();
			reader.readAsDataURL(file);
			reader.onload = () => resolve(reader.result);
			reader.onerror = error => reject(error);
		});
		let base64String = "";
		let photoToBeSave = "";
		if (filesMemo.length > 0) {
			base64String = await toBase64(filesMemo[0]);
			photoToBeSave = base64String.split(',')[1];
		}
		const data = {
			title: description,
			dateFrom: dateFrom,
			dateTo: dateTo,
			description: contentMemo,
			content: filesMemo ? photoToBeSave : null,
		};
		try {
			setLoadingRequest(true);
			await axios.put(`${apiUrl}/${editDataMemo.id}`, data, {
				headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }
			});
			setShowEditMemo(false);
			showGeneralMessage("Request Success!", true);
			resetFormFields();
			setIsUpdateMemo(true);
			setIsRefreshEvents(true);
		} catch (error) {
			console.error('Error:', error);
			const errorMessage = JSON.parse(error.response.request.responseText).message;
			showGeneralMessage(errorMessage, false);
		} finally {
			setLoadingRequest(false);
		}
	};

	const handleDeleteMemo = async (event) => {
		event.preventDefault();
		const apiUrl = domain + "/api/Memo";
		try {
			setLoadingRequest(true);
			await axios.delete(`${apiUrl}/${editDataMemo.id}`, {
				headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }
			});
			setShowDeleteMemo(false);
			await SetAuditTrail({ setupArea: "Calendar", actionTaken: "delete", field: "Delete", newValue: `-`, oldValue: `${editDataMemo.title}`, dateSaved: new Date(), savedById: user.id });
			showGeneralMessage("Request Success!", true);
			resetFormFields();
			setIsUpdateMemo(true);
			setIsRefreshEvents(true);
		} catch (error) {
			console.error('Error:', error);
			const errorMessage = JSON.parse(error.response.request.responseText).message;
			showGeneralMessage(errorMessage, false);
		} finally {
			setLoadingRequest(false);
		}
	};

	// ─── PATCHED: EMPLOYEE MEMO HANDLERS ──────────────────────────────────────
	const handleSubmitEmployeeMemo = async (event) => {
		event.preventDefault();
		const apiUrl = domain + "/api/EmployeeMemo";
		const toBase64 = (file) => new Promise((resolve, reject) => {
			const reader = new FileReader();
			reader.readAsDataURL(file);
			reader.onload = () => resolve(reader.result);
			reader.onerror = error => reject(error);
		});
		let base64String = "";
		let photoToBeSave = "";
		if (filesMemo.length > 0) {
			base64String = await toBase64(filesMemo[0]);
			photoToBeSave = base64String.split(',')[1];
		}

		// title removed, memoTypeId added
		const data = {
			memoTypeId: selectedMemoTypeId ? parseInt(selectedMemoTypeId) : null,
			dateFrom: dateFrom,
			dateTo: dateTo,
			description: contentMemo,
			content: filesMemo.length > 0 ? photoToBeSave : null,
			employeePersonalInformationId: selectedEmployeePersonalInformationId,
			// ─── PATCHED: include CC ───────────────────────────────────────────────
			ccEmployeeIds: ccList.map(e => e.id),
			// ─────────────────────────────────────────────────────────────────────
		};

		try {
			setLoadingRequest(true);
			await axios.post(apiUrl, data, {
				headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }
			});
			setShowEmployeeMemoForm(false);
			// Audit trail uses memoTypeName for readability
			const memoTypeName = memoTypes.find(mt => mt.id === data.memoTypeId)?.name ?? `MemoTypeId: ${data.memoTypeId}`;
			await SetAuditTrail({ setupArea: "Calendar", actionTaken: "insert", field: "Add", newValue: memoTypeName, oldValue: "-", dateSaved: new Date(), savedById: user.id });
			showGeneralMessage("Request Success!", true);
			resetFormFields();
			setIsRefreshEvents(true);
		} catch (error) {
			console.error('Error:', error);
			const errorMessage = JSON.parse(error.response.request.responseText).message;
			showGeneralMessage(errorMessage, false);
		} finally {
			setLoadingRequest(false);
		}
	};

	const handleUpdateEmployeeMemo = async (event) => {
		event.preventDefault();
		const apiUrl = domain + "/api/EmployeeMemo";
		const toBase64 = (file) => new Promise((resolve, reject) => {
			const reader = new FileReader();
			reader.readAsDataURL(file);
			reader.onload = () => resolve(reader.result);
			reader.onerror = error => reject(error);
		});
		let base64String = "";
		let photoToBeSave = "";
		if (filesMemo.length > 0) {
			base64String = await toBase64(filesMemo[0]);
			photoToBeSave = base64String.split(',')[1];
		}

		// title removed, memoTypeId added
		const data = {
			memoTypeId: selectedMemoTypeId ? parseInt(selectedMemoTypeId) : null,
			dateFrom: dateFrom,
			dateTo: dateTo,
			description: contentMemo,
			content: filesMemo.length > 0 ? photoToBeSave : (editDataEmployeeMemo?.content ?? null),
			employeePersonalInformationId: selectedEmployeePersonalInformationId,
			// ─── PATCHED: include CC ───────────────────────────────────────────────
			ccEmployeeIds: ccList.map(e => e.id),
			// ─────────────────────────────────────────────────────────────────────
		};

		try {
			setLoadingRequest(true);
			await axios.put(`${apiUrl}/${editDataEmployeeMemo.id}`, data, {
				headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }
			});
			setShowEditEmployeeMemo(false);
			showGeneralMessage("Request Success!", true);
			resetFormFields();
			setIsUpdateEmployeeMemo(true);
			setIsRefreshEvents(true);
		} catch (error) {
			console.error('Error:', error);
			const errorMessage = JSON.parse(error.response.request.responseText).message;
			showGeneralMessage(errorMessage, false);
		} finally {
			setLoadingRequest(false);
		}
	};

	const handleDeleteEmployeeMemo = async (event) => {
		event.preventDefault();
		const apiUrl = domain + "/api/EmployeeMemo";
		try {
			setLoadingRequest(true);
			await axios.delete(`${apiUrl}/${editDataEmployeeMemo.id}`, {
				headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }
			});
			setShowDeleteEmployeeMemo(false);
			// Audit trail uses memoTypeName since title no longer exists
			const memoTypeName = editDataEmployeeMemo.memoTypeName ?? `MemoTypeId: ${editDataEmployeeMemo.memoTypeId}`;
			await SetAuditTrail({ setupArea: "Calendar", actionTaken: "delete", field: "Delete", newValue: `-`, oldValue: memoTypeName, dateSaved: new Date(), savedById: user.id });
			showGeneralMessage("Request Success!", true);
			resetFormFields();
			setIsUpdateEmployeeMemo(true);
			setIsRefreshEvents(true);
		} catch (error) {
			console.error('Error:', error);
			const errorMessage = JSON.parse(error.response.request.responseText).message;
			showGeneralMessage(errorMessage, false);
		} finally {
			setLoadingRequest(false);
		}
	};
	// ──────────────────────────────────────────────────────────────────────────

	// --- BUTTON CLICK HANDLERS ---
	const todayHandlerClick = () => { setSelectedDate(new Date()); };
	const previousHandlerClick = () => {
		const newDate = new Date(selectedDate.getFullYear(), selectedDate.getMonth() - 1, 1);
		setSelectedDate(newDate);
	};
	const nextHandlerClick = () => {
		const newDate = new Date(selectedDate.getFullYear(), selectedDate.getMonth() + 1, 1);
		setSelectedDate(newDate);
	};

	const addAnnouncementHandlerClick = () => { resetFormFields(); setShowAnnouncementForm(true); };
	const closeAnnouncementFormHandlerClick = () => { setShowAnnouncementForm(false); resetFormFields(); };
	const addMemoHandlerClick = () => { resetFormFields(); setShowMemoForm(true); };
	const closeMemoFormHandlerClick = () => { setShowMemoForm(false); resetFormFields(); };
	const addGeneralAnnouncementHandlerClick = () => { setShowGeneralAnnouncementForm(true); };
	const closeGeneralAnnouncementFormHandlerClick = () => { setShowGeneralAnnouncementForm(false); };
	const addGeneralMemoHandlerClick = () => { setShowGeneralMemoForm(true); };
	const closeGeneralMemoFormHandlerClick = () => { setShowGeneralMemoForm(false); };
	const addEventsAnnouncementHandlerClick = (date) => { setEventDate(date); setShowEventsAnnouncement(true); };
	const closeEventsAnnouncementHandlerClick = () => { setEventDate(""); setShowEventsAnnouncement(false); };
	const addEventsMemoHandlerClick = (date) => { setEventDate(date); setShowEventsMemo(true); };
	const closeEventsMemoHandlerClick = () => { setEventDate(""); setShowEventsMemo(false); };
	const addAllEventsAnnouncementHandlerClick = () => { setShowAllEventsAnnouncement(true); };
	const closeAllEventsAnnouncementHandlerClick = () => { setShowAllEventsAnnouncement(false); };
	const addAllEventsMemoHandlerClick = () => { setShowAllEventsMemo(true); };
	const closeAllEventsMemoHandlerClick = () => { setShowAllEventsMemo(false); };
	const addEmployeeMemoHandlerClick = () => { resetFormFields(); setShowEmployeeMemoForm(true); };
	const closeEmployeeMemoFormHandlerClick = () => { setShowEmployeeMemoForm(false); resetFormFields(); };
	const addEventsEmployeeMemoHandlerClick = (date) => { setEventDate(date); setShowEventsEmployeeMemo(true); };
	const closeEventsEmployeeMemoHandlerClick = () => { setEventDate(""); setShowEventsEmployeeMemo(false); };
	const addAllEventsEmployeeMemoHandlerClick = () => { setShowAllEventsEmployeeMemo(true); };
	const closeAllEventsEmployeeMemoHandlerClick = () => { setShowAllEventsEmployeeMemo(false); };

	// ─── PATCHED: editHandleClickEmployeeMemo uses memoTypeId not title ────────
	const editHandleClickEmployeeMemo = (data) => {
		setEditDataEmployeeMemo(data);
		setSelectedMemoTypeId(data.memoTypeId ? data.memoTypeId.toString() : "");
		setDateFrom(data.dateFrom);
		setDateTo(data.dateTo);
		setContentMemo(data.description);
		setSelectedEmployeePersonalInformationId(data.employeePersonalInformationId);
		// ─── PATCHED: populate CC list from existing memo data ────────────────
		setCcList(
			(data.ccList ?? []).map(cc => ({
				id: cc.id,
				fullName: cc.employeeFullName,
				employeeId: cc.employeeId
			}))
		);
		// ─────────────────────────────────────────────────────────────────────
		if (data.content) {
			let filesToBeSet = [];
			try {
				const binaryData = atob(data.content);
				const arrayBuffer = new ArrayBuffer(binaryData.length);
				const uint8Array = new Uint8Array(arrayBuffer);
				for (let i = 0; i < binaryData.length; i++) {
					uint8Array[i] = binaryData.charCodeAt(i);
				}
				const blob = new Blob([uint8Array], { type: 'image/jpeg' });
				const file = new File([blob], 'memo_attachment.jpg', { type: 'image/jpeg' });
				filesToBeSet.push(file);
			} catch (error) {
				console.error("Error converting base64 to file:", error);
			}
			setFilesMemo(filesToBeSet);
		} else {
			setFilesMemo([]);
		}
		setShowEditEmployeeMemo(true);
	};
	// ──────────────────────────────────────────────────────────────────────────

	const closeEditEmployeeMemoFormHandlerClick = () => { setShowEditEmployeeMemo(false); resetFormFields(); };
	const deleteHandleClickEmployeeMemo = (data) => { setEditDataEmployeeMemo(data); setShowDeleteEmployeeMemo(true); };
	const closeDeleteEmployeeMemoFormHandlerClick = () => { setShowDeleteEmployeeMemo(false); setEditDataEmployeeMemo([]); };

	const editHandleClickAnnouncement = (data) => { setEditDataAnnouncement(data); setShowEditAnnouncement(true); };
	const closeEditAnnouncementFormHandlerClick = () => { setShowEditAnnouncement(false); resetFormFields(); };

	useEffect(() => {
		if (showEditAnnouncement && editDataAnnouncement) {
			let filesToBeSet = [];
			setDescription(editDataAnnouncement.title || "");
			setDateFrom(editDataAnnouncement.dateFrom || "");
			setDateTo(editDataAnnouncement.dateTo || "");
			setContentAnnouncement(editDataAnnouncement.description || "");
			if (editDataAnnouncement.content) {
				try {
					const binaryData = atob(editDataAnnouncement.content);
					const arrayBuffer = new ArrayBuffer(binaryData.length);
					const uint8Array = new Uint8Array(arrayBuffer);
					for (let i = 0; i < binaryData.length; i++) { uint8Array[i] = binaryData.charCodeAt(i); }
					const blob = new Blob([uint8Array], { type: 'image/jpeg' });
					const file = new File([blob], 'filename.jpg', { type: 'image/jpeg' });
					filesToBeSet.push(file);
				} catch (e) { console.error(e); }
			}
			setFilesAnnouncement(filesToBeSet);
		}
	}, [showEditAnnouncement, editDataAnnouncement]);

	const deleteHandleClickAnnouncement = (data) => { setEditDataAnnouncement(data); setShowDeleteAnnouncement(true); };
	const closeDeleteAnnouncementFormHandlerClick = () => { setShowDeleteAnnouncement(false); resetFormFields(); };
	const editHandleClickMemo = (data) => { setEditDataMemo(data); setShowEditMemo(true); };
	const closeEditMemoFormHandlerClick = () => { setShowEditMemo(false); resetFormFields(); };

	useEffect(() => {
		if (showEditMemo && editDataMemo) {
			let filesToBeSet = [];
			setDescription(editDataMemo.title || "");
			setDateFrom(editDataMemo.dateFrom || "");
			setDateTo(editDataMemo.dateTo || "");
			setContentMemo(editDataMemo.description || "");
			if (editDataMemo.content) {
				try {
					const binaryData = atob(editDataMemo.content);
					const arrayBuffer = new ArrayBuffer(binaryData.length);
					const uint8Array = new Uint8Array(arrayBuffer);
					for (let i = 0; i < binaryData.length; i++) { uint8Array[i] = binaryData.charCodeAt(i); }
					const blob = new Blob([uint8Array], { type: 'image/jpeg' });
					const file = new File([blob], 'filename.jpg', { type: 'image/jpeg' });
					filesToBeSet.push(file);
				} catch (e) { console.error(e); }
			}
			setFilesMemo(filesToBeSet);
		}
	}, [showEditMemo, editDataMemo]);

	const deleteHandleClickMemo = (data) => { setEditDataMemo(data); setShowDeleteMemo(true); };
	const closeDeleteMemoFormHandlerClick = () => { setShowDeleteMemo(false); resetFormFields(); };

	useEffect(() => {
		const fetchEmployees = async () => {
			try {
				const response = await axios.get(`${domain}/api/Employee`, {
					headers: { 'Authorization': `Bearer ${token}` }
				});
				setEmployees(response.data);
			} catch (error) {
				console.error('Error fetching employees:', error);
			}
		};
		fetchEmployees();
	}, []);

	return (
		<div>
			<div className={`MainBody row ${isHideSideMenu ? "MainBody2" : ""}`}>
				<div className="LeftSpace"></div>
				<div className={`BodySpace ${isHideSideMenu ? "BodySpace2" : ""}`}>
					<div className="ReportsInfo shadow-xl">
						<div className="reports-header">
							<h3>Scheduler Page</h3>
						</div>
						<div className="reports-body">
							<CalendarHeader
								todayHandlerClick={todayHandlerClick}
								previousHandlerClick={previousHandlerClick}
								nextHandlerClick={nextHandlerClick}
								addAnnouncementHandlerClick={addAnnouncementHandlerClick}
								addMemoHandlerClick={addMemoHandlerClick}
								addEmployeeMemoHandlerClick={addEmployeeMemoHandlerClick}
								addGeneralAnnouncementHandlerClick={addGeneralAnnouncementHandlerClick}
								addGeneralMemoHandlerClick={addGeneralMemoHandlerClick}
								addAllEventsAnnouncementHandlerClick={addAllEventsAnnouncementHandlerClick}
								addAllEventsMemoHandlerClick={addAllEventsMemoHandlerClick}
								addAllEventsEmployeeMemoHandlerClick={addAllEventsEmployeeMemoHandlerClick}
								userAccess={userAccess}
							/>
							<div className='flex justify-center md:justify-start items-center mb-2'>
								<MonthYearPicker selectedDate={selectedDate} setSelectedDate={setSelectedDate} />
							</div>
							<CalendarBody selectedDate={selectedDate}
								date={date}
								setDate={setDate}
								addEventsAnnouncementHandlerClick={addEventsAnnouncementHandlerClick}
								addEventsMemoHandlerClick={addEventsMemoHandlerClick}
								addEventsEmployeeMemoHandlerClick={addEventsEmployeeMemoHandlerClick}
								isRefreshEvents={isRefreshEvents}
								setIsRefreshEvents={setIsRefreshEvents}
								userAccess={userAccess}
							/>
						</div>
					</div>
				</div>
			</div>

			<ViewEventsAnnouncementForm isOpen={showEventsAnnouncement}
				onClose={closeEventsAnnouncementHandlerClick}
				eventDate={eventDate}
				editHandleClick={editHandleClickAnnouncement}
				deleteHandleClick={deleteHandleClickAnnouncement}
				isUpdate={isUpdateAnnouncement}
				setIsUpdate={setIsUpdateAnnouncement}
				userAccess={userAccess}
			/>
			<ViewEventsMemoForm isOpen={showEventsMemo}
				onClose={closeEventsMemoHandlerClick}
				eventDate={eventDate}
				editHandleClick={editHandleClickMemo}
				deleteHandleClick={deleteHandleClickMemo}
				isUpdate={isUpdateMemo}
				setIsUpdate={setIsUpdateMemo}
				userAccess={userAccess}
			/>
			<ViewAllEventsAnnouncementForm isOpen={showAllEventsAnnouncement}
				onClose={closeAllEventsAnnouncementHandlerClick}
				editHandleClick={editHandleClickAnnouncement}
				deleteHandleClick={deleteHandleClickAnnouncement}
				isUpdate={isUpdateAnnouncement}
				setIsUpdate={setIsUpdateAnnouncement}
				userAccess={userAccess}
			/>
			<ViewAllEventsMemoForm isOpen={showAllEventsMemo}
				onClose={closeAllEventsMemoHandlerClick}
				editHandleClick={editHandleClickMemo}
				deleteHandleClick={deleteHandleClickMemo}
				isUpdate={isUpdateMemo}
				setIsUpdate={setIsUpdateMemo}
				userAccess={userAccess}
			/>
			<ViewEventsEmployeeMemoForm
				isOpen={showEventsEmployeeMemo}
				onClose={closeEventsEmployeeMemoHandlerClick}
				eventDate={eventDate}
				editHandleClick={editHandleClickEmployeeMemo}
				deleteHandleClick={deleteHandleClickEmployeeMemo}
				isUpdate={isUpdateEmployeeMemo}
				setIsUpdate={setIsUpdateEmployeeMemo}
				userAccess={userAccess}
			/>
			<ViewAllEventsEmployeeMemoForm
				isOpen={showAllEventsEmployeeMemo}
				onClose={closeAllEventsEmployeeMemoHandlerClick}
				editHandleClick={editHandleClickEmployeeMemo}
				deleteHandleClick={deleteHandleClickEmployeeMemo}
				isUpdate={isUpdateEmployeeMemo}
				setIsUpdate={setIsUpdateEmployeeMemo}
				userAccess={userAccess}
			/>

			<AddEditAnnouncementForm
				isOpen={showAnnouncementForm || showEditAnnouncement}
				isEdit={showEditAnnouncement}
				onClose={showEditAnnouncement ? closeEditAnnouncementFormHandlerClick : closeAnnouncementFormHandlerClick}
				description={description}
				dateFrom={dateFrom}
				dateTo={dateTo}
				contentAnnouncement={contentAnnouncement}
				files={filesAnnouncement}
				setDescription={setDescription}
				setDateFrom={setDateFrom}
				setDateTo={setDateTo}
				setContentAnnouncement={setContentAnnouncement}
				setFiles={setFilesAnnouncement}
				handleSubmitAnnouncement={showEditAnnouncement ? handleUpdateAnnouncement : handleSubmitAnnouncement}
			/>
			<AddEditMemoForm
				isOpen={showMemoForm || showEditMemo}
				isEdit={showEditMemo}
				onClose={showEditMemo ? closeEditMemoFormHandlerClick : closeMemoFormHandlerClick}
				description={description}
				dateFrom={dateFrom}
				dateTo={dateTo}
				contentMemo={contentMemo}
				files={filesMemo}
				setDescription={setDescription}
				setDateFrom={setDateFrom}
				setDateTo={setDateTo}
				setContentMemo={setContentMemo}
				setFiles={setFilesMemo}
				handleSubmitMemo={showEditMemo ? handleUpdateMemo : handleSubmitMemo}
			/>

			{/* ─── PATCHED: description replaced with selectedMemoTypeId + memoTypes ── */}
			<AddEditEmployeeMemoForm
				isOpen={showEmployeeMemoForm || showEditEmployeeMemo}
				isEdit={showEditEmployeeMemo}
				onClose={showEditEmployeeMemo ? closeEditEmployeeMemoFormHandlerClick : closeEmployeeMemoFormHandlerClick}
				memoTypeId={selectedMemoTypeId}
				setMemoTypeId={setSelectedMemoTypeId}
				memoTypes={memoTypes}
				dateFrom={dateFrom}
				dateTo={dateTo}
				contentMemo={contentMemo}
				files={filesMemo}
				setDateFrom={setDateFrom}
				setDateTo={setDateTo}
				setContentMemo={setContentMemo}
				setFiles={setFilesMemo}
				selectedEmployeePersonalInformationId={selectedEmployeePersonalInformationId}
				setSelectedEmployeePersonalInformationId={setSelectedEmployeePersonalInformationId}
				editDataEmployeeMemo={editDataEmployeeMemo}  // ADDED - replaces employees prop
				ccList={ccList}
				setCcList={setCcList}
				isActive={isActive}                          // ADDED - was missing
				setIsActive={setIsActive}                    // ADDED - was missing
				handleSubmitMemo={showEditEmployeeMemo ? handleUpdateEmployeeMemo : handleSubmitEmployeeMemo}
			/>
			{/* ──────────────────────────────────────────────────────────────────────── */}

			<AddGeneralAnnouncementForm isOpen={showGeneralAnnouncementForm}
				onClose={closeGeneralAnnouncementFormHandlerClick}
				description={descriptionGeneralAnnouncement}
				date={date}
				contentAnnouncement={contentGeneralAnnouncement}
				files={filesGeneralAnnouncement}
				setDescription={setDescriptionGeneralAnnouncement}
				setDate={setDate}
				setContentAnnouncement={setContentGeneralAnnouncement}
				setFiles={setFilesGeneralAnnouncement}
				setGeneralData={setGeneralData}
				handleSubmitAnnouncementCreate={handleSubmitGeneralAnnouncementCreate}
				handleSubmitAnnouncementUpdate={handleSubmitGeneralAnnouncementUpdate}
				handleSubmitGeneralDelete={handleSubmitGeneralDelete}
				userAccess={userAccess}
			/>
			<AddGeneralMemoForm isOpen={showGeneralMemoForm}
				onClose={closeGeneralMemoFormHandlerClick}
				description={descriptionGeneralMemo}
				date={date}
				contentMemo={contentGeneralMemo}
				files={filesGeneralMemo}
				setDescription={setDescriptionGeneralMemo}
				setDate={setDate}
				setContentMemo={setContentGeneralMemo}
				setFiles={setFilesGeneralMemo}
				setGeneralData={setGeneralData}
				handleSubmitMemoCreate={handleSubmitGeneralMemoCreate}
				handleSubmitMemoUpdate={handleSubmitGeneralMemoUpdate}
				handleSubmitGeneralDelete={handleSubmitGeneralDelete}
				userAccess={userAccess}
			/>

			<DeleteAnnouncementForm isOpen={showDeleteAnnouncement}
				onClose={closeDeleteAnnouncementFormHandlerClick}
				handleDelete={handleDeleteAnnouncement}
			/>
			<DeleteMemoForm isOpen={showDeleteMemo}
				onClose={closeDeleteMemoFormHandlerClick}
				handleDelete={handleDeleteMemo}
			/>
			<DeleteEmployeeMemoForm
				isOpen={showDeleteEmployeeMemo}
				onClose={closeDeleteEmployeeMemoFormHandlerClick}
				handleDelete={handleDeleteEmployeeMemo}
			/>

			<GeneralMessage form={showMessage} message={message} isSuccess={isSuccess} />
			<LoadingSpinner loading={loadingRequest} />
		</div>
	);
}

const CustomInput = forwardRef(({ value, onClick }, ref) => (
	<button
		className="border-0 bg-transparent text-[#668fca] text-4xl"
		onClick={onClick}
		ref={ref}
	>
		{value}
	</button>
));

export default Calendar;